import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Navbar.css';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const toggleRef = useRef(null);
  const [appName, setAppName] = useState('BookSummary');

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsMenuOpen(false);
  };

  const handleReload = () => {
    window.location.reload();
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    console.log('Menu toggled, isMenuOpen:', !isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Determine app name based on URL
  useEffect(() => {
    const url = window.location.href.toLowerCase();
    const hasAISparkNotes = url.includes('aisparknotes');
    setAppName(hasAISparkNotes ? 'AISparkNotes' : 'BookSummary');
  }, [location]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMenuOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        toggleRef.current &&
        !toggleRef.current.contains(event.target)
      ) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Mobile menu content to be rendered via portal
  const mobileMenuContent = user && isMenuOpen ? (
    <>
      <div className="navbar-overlay" onClick={closeMenu}></div>
      <div 
        ref={menuRef} 
        className={`navbar-menu active`}
      >
        <Link to="/" className="navbar-link" onClick={closeMenu}>Dashboard</Link>
        <Link to="/search" className="navbar-link" onClick={closeMenu}>Search Books</Link>
        <Link to="/saved" className="navbar-link" onClick={closeMenu}>Saved Books</Link>
        <Link to="/summaries" className="navbar-link" onClick={closeMenu}>My Summaries</Link>
        <Link to="/settings" className="navbar-link" onClick={closeMenu}>Settings</Link>
        {user?.email?.toLowerCase() === 'benjamin@universallythinking.com' && (
          <Link to="/admin" className="navbar-link" onClick={closeMenu}>Admin</Link>
        )}
        <span className="navbar-user">Hello, {user.name || user.email}</span>
        <button onClick={handleReload} className="btn btn-secondary navbar-reload">
          Reload
        </button>
        <button onClick={handleLogout} className="btn btn-secondary navbar-logout">
          Logout
        </button>
      </div>
    </>
  ) : null;

  return (
    <>
      <nav className="navbar">
        <div className="navbar-container">
          <Link to="/" className="navbar-brand" onClick={closeMenu}>
            {appName}
          </Link>
          {user && (
            <>
              <button 
                ref={toggleRef}
                className="navbar-toggle" 
                onClick={toggleMenu}
                aria-label="Toggle menu"
                aria-expanded={isMenuOpen}
              >
                <span className={isMenuOpen ? 'hamburger open' : 'hamburger'}>
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </button>
              {/* Desktop menu - rendered inline */}
              <div className="navbar-menu navbar-menu-desktop">
                <Link to="/" className="navbar-link" onClick={closeMenu}>Dashboard</Link>
                <Link to="/search" className="navbar-link" onClick={closeMenu}>Search Books</Link>
                <Link to="/saved" className="navbar-link" onClick={closeMenu}>Saved Books</Link>
                <Link to="/summaries" className="navbar-link" onClick={closeMenu}>My Summaries</Link>
                <Link to="/settings" className="navbar-link" onClick={closeMenu}>Settings</Link>
                {user?.email?.toLowerCase() === 'benjamin@universallythinking.com' && (
                  <Link to="/admin" className="navbar-link" onClick={closeMenu}>Admin</Link>
                )}
                <span className="navbar-user">Hello, {user.name || user.email}</span>
                <button onClick={handleReload} className="btn btn-secondary navbar-reload">
                  Reload
                </button>
                <button onClick={handleLogout} className="btn btn-secondary navbar-logout">
                  Logout
                </button>
              </div>
            </>
          )}
        </div>
      </nav>
      {/* Mobile menu - rendered via portal to document.body */}
      {typeof window !== 'undefined' && createPortal(mobileMenuContent, document.body)}
    </>
  );
};

export default Navbar;
